import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ParticipantRoutePreferenceDto,
  deleteParticipantRoutePreference,
  fetchParticipantPreferences,
  makeRouteDisfavored,
  makeRoutePreferred,
} from '~/generated';
import { extractData } from '~/utils/query-utils';

export type RoutePreferenceValue = 'preferred' | 'neutral' | 'disfavored';

export type SaveRoutePreferenceVariables = {
  routeId: number;
  value: RoutePreferenceValue;
};

export const routePreferencesQueryKey = (participantId: number | undefined) =>
  ['routePreferences', participantId] as const;

export const toRoutePreferenceValue = (
  pref: ParticipantRoutePreferenceDto | undefined
): RoutePreferenceValue => {
  if (pref?.isPreferred) return 'preferred';
  if (pref?.isDisfavored) return 'disfavored';
  return 'neutral';
};

export const useRoutePreferences = (participantId: number | undefined) =>
  useQuery({
    queryKey: routePreferencesQueryKey(participantId),
    queryFn: async () => {
      const response = await extractData(
        fetchParticipantPreferences({
          path: { participantId: participantId! },
        })
      );
      return response.preferences ?? [];
    },
    enabled: Boolean(participantId),
  });

type SaveContext = {
  previous: ParticipantRoutePreferenceDto[] | undefined;
};

export const useSaveRoutePreference = (
  participantId: number | undefined,
  options: UseMutationOptions<
    void,
    Error,
    SaveRoutePreferenceVariables,
    SaveContext
  > = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = routePreferencesQueryKey(participantId);

  return useMutation<void, Error, SaveRoutePreferenceVariables, SaveContext>({
    scope: { id: 'routePreferences' },
    mutationKey: [...queryKey, 'save'],
    mutationFn: async ({ routeId, value }) => {
      if (participantId === undefined) {
        throw new Error('participantId is required');
      }
      if (value === 'preferred') {
        await extractData(
          makeRoutePreferred({ path: { participantId, routeId } })
        );
        return;
      }
      if (value === 'disfavored') {
        await extractData(
          makeRouteDisfavored({ path: { participantId, routeId } })
        );
        return;
      }
      await extractData(
        deleteParticipantRoutePreference({
          path: { participantId, routeId },
        })
      );
    },
    ...options,
    async onMutate(...args) {
      const [variables] = args;
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<ParticipantRoutePreferenceDto[]>(queryKey);
      queryClient.setQueryData<ParticipantRoutePreferenceDto[]>(
        queryKey,
        (rows = []) => {
          const without = rows.filter((r) => r.routeId !== variables.routeId);
          if (variables.value === 'neutral') return without;
          return [
            ...without,
            {
              participantId,
              routeId: variables.routeId,
              isPreferred: variables.value === 'preferred',
              isDisfavored: variables.value === 'disfavored',
            },
          ];
        }
      );
      await options.onMutate?.(...args);
      return { previous };
    },
    onError(...args) {
      const [, , context] = args;
      queryClient.setQueryData(queryKey, context?.previous);
      options.onError?.(...args);
    },
  });
};
